import { useState, useRef } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import * as t from './useAIGeneration.types';

export const useAIGeneration = <
  TStartParams,
  TStartReturn,
  TStatusReturn extends { status: t.GenerationStatus },
  TResultReturn,
  TRateParams,
  TRateReturn,
>({
  startGenerationRequest,
  retryGenerationRequest,
  checkStatusRequest,
  getResultRequest,
  rateGenerationRequest,
}: t.Props<TStartParams, TStartReturn, TStatusReturn, TResultReturn, TRateParams, TRateReturn>) => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<t.State>('idle');
  const [generationData, setGenerationData] = useState<TStartReturn | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const startGeneration: t.StartGeneration<TStartParams, TStartReturn> = useMutation({
    mutationKey: ['startGeneration'],
    mutationFn: async (params) => {
      const data = await startGenerationRequest(params.data, params.signal);
      if (!data) {
        throw new Error('Invalid response from generation start');
      }
      return data;
    },
  });

  const checkStatus: t.CheckStatus<TStartReturn> = async (params) => {
    if (!params.data) {
      throw new Error('Generation data is not available for status check');
    }
    const queryKey = ['generationStatus', params.data];
    const statusData = await queryClient.fetchQuery(queryKey, async () => {
      const data = await checkStatusRequest(params.data, params.signal);
      if (data && 'status' in data) {
        return data.status as t.GenerationStatus;
      } else {
        throw new Error('Invalid response from check status');
      }
    });
    return statusData;
  };

  const getResult: t.GetResult<TStartReturn, TResultReturn> = async (params) => {
    if (!params.data) {
      throw new Error('Generation data is not available for result fetch');
    }
    const queryKey = ['generationResult', params.data];
    const data = await queryClient.fetchQuery(queryKey, async () => {
      const resultData = await getResultRequest(params.data, params.signal);
      if (resultData) {
        return resultData;
      } else {
        throw new Error('Invalid response from result fetch');
      }
    });
    return data;
  };

  const retryGeneration: t.RetryGeneration<TStartReturn, TStartReturn> = useMutation({
    mutationKey: ['retryGeneration', generationData],
    mutationFn: async (params) => {
      if (!params.data) {
        throw new Error('Generation data is not available for retry');
      }
      const retryData = await retryGenerationRequest(params.data, params.signal);
      if (!retryData) {
        throw new Error('Invalid response from retry request');
      }
      setGenerationData(retryData);
      return retryData;
    },
  });

  const rateGeneration: t.RateGeneration<TRateParams, TRateReturn> = useMutation({
    mutationKey: ['rateGeneration', generationData],
    mutationFn: async (params) => {
      if (!generationData) {
        throw new Error('Generation data is not available for rate');
      }

      const data = await rateGenerationRequest({ generationData: generationData!, aditionalData: params });
      if (data) {
        return data;
      } else {
        throw new Error('Rating request failed');
      }
    },
  });

  // Helper function to manage polling attempts
  const pollWithAttempts: t.PollWithAttempts<TStartReturn, TResultReturn> = async (params) => {
    const { data, maxAttempts, pollInterval, signal } = params;
    let attempts = 0;
    while (attempts < maxAttempts) {
      if (signal.aborted) {
        return null;
      }
      const generationStatus = await checkStatus({ data, signal });
      if (generationStatus === 'completed') {
        const resultResponse = await getResult({ data, signal });
        return resultResponse;
      } else if (generationStatus === 'pending') {
        attempts++;
        await new Promise((resolve) => setTimeout(resolve, pollInterval));
        continue;
      }

      throw new Error(`Generation failed with status: ${generationStatus}`);
    }
    throw new Error('Max attempts reached while polling for status');
  };

  const generateAndPoll: t.GenerateAndPoll<TStartParams, TResultReturn> = async (params) => {
    try {
      const { startParams, pollInterval = 2000, maxAttempts = 60 * 3 } = params;
      setState('generating');
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      const startResponse = await startGeneration.mutateAsync({
        data: startParams,
        signal,
      });
      setGenerationData(startResponse);
      const response = await pollWithAttempts({ data: startResponse, maxAttempts, pollInterval, signal });
      if (response) {
        setState('completed');
      }
      return response;
    } catch (e) {
      setState('error');
      throw e;
    }
  };

  const retryAndPoll: t.RetryAndPoll<TResultReturn> = async (params) => {
    try {
      const { pollInterval = 2000, maxAttempts = 60 } = params || {};
      if (!generationData) {
        throw new Error('Generation data is not available for retry and poll');
      }
      setState('retrying');
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;
      const retryResponse = await retryGeneration.mutateAsync({ data: generationData, signal });
      const response = await pollWithAttempts({ data: retryResponse, maxAttempts, pollInterval, signal });
      if (response) {
        setState('completed');
      }
      return response;
    } catch (e) {
      setState('error');
      throw e;
    }
  };

  const cancelGeneration: t.CancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setState('idle');
      setGenerationData(null);
      abortControllerRef.current = null;
    }
  };

  const changeState = (state: t.State) => {
    setState(state);
  };

  return {
    generationData,
    state,
    generateAndPoll,
    retryAndPoll,
    cancelGeneration,
    rateGeneration,
    changeState,
  };
};
